import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  type OnDestroy,
} from '@angular/core';
import {
  ChartFacade,
  TreatmentPlanFacade,
  TreatmentStepsFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  TreatmentPlan,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  AppointmentStatus,
  isTreatmentPlan,
  StepAddPosition,
  type IChartedTreatmentSelectionSummary,
  type IPatient,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  addDoc,
  asyncForEach,
  ISelectOption,
  isSameRef,
  isWithRef,
  patchDoc,
  snapshot,
  type DocumentReference,
  type IGroup,
  type WithRef,
} from '@principle-theorem/shared';
import { SelectionListStore } from '@principle-theorem/ng-shared';
import { compact, uniqBy } from 'lodash';
import { noop, type Observable, of, Subject } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';
import {
  ChartableSurfaceUpdater,
  type IEditChartableData,
} from '../../chartable-surface-updater';
import { AddTreatmentToStoreStepProvider } from '../../charted-surface/store-treatment-plan/add-treatment-to-store-step-provider';
import {
  ConsolidatedPlanDragDropGroup,
  type IConsolidatedPlanDragDropNode,
} from '../../consolidated-plan-drag-drop-group';
import { type IDropEvent } from '../../drag-drop-group';
import { StepDragDropGroup } from '../../step-drag-drop-group';
import { TreatmentProviderService } from '../../treatment-provider.service';
import { TreatmentStepsDisplayService } from '../treatment-steps-editor/treatment-steps-display.service';
import {
  TreatmentPlansConsolidatedStore,
  type ITreatmentPlanStepPair,
} from './treatment-plans-consolidated.store';

@Component({
  selector: 'pr-treatment-plans-consolidated',
  templateUrl: './treatment-plans-consolidated.component.html',
  styleUrls: ['./treatment-plans-consolidated.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentPlansConsolidatedComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private readonly _selectionList = inject(
    SelectionListStore<IChartedTreatmentSelectionSummary>,
    { optional: true }
  );
  private readonly _treatmentProvider = inject(TreatmentProviderService);
  private readonly _treatmentPlanFacade = inject(TreatmentPlanFacade);
  dragDrop = new ConsolidatedPlanDragDropGroup();
  treatmentPlanManagePermission = [PatientPermissions.TreatmentPlanManage];
  hasSelection$: Observable<boolean>;
  @Input() stepDragDrop = new StepDragDropGroup();
  @Output() selectedTreatmentPlan = new EventEmitter<WithRef<ITreatmentPlan>>();

  @Input()
  set patient(patient: WithRef<IPatient>) {
    if (patient) {
      this.store.initialiseStore(patient);
    }
  }

  constructor(
    public store: TreatmentPlansConsolidatedStore,
    public stepDisplay: TreatmentStepsDisplayService,
    private _treatmentStepsFacade: TreatmentStepsFacade,
    private _chartStore: ChartFacade
  ) {
    this.hasSelection$ =
      this._selectionList?.selected$.pipe(
        map((selection) => selection.length > 0)
      ) ?? of(false);

    this.dragDrop.afterDrop$
      .pipe(
        concatMap((event) => this._handleStepDrag(event)),
        takeUntil(this._onDestroy$)
      )
      .subscribe(noop);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  dragDisabled(pair: ITreatmentPlanStepPair): boolean {
    if (pair.appointment) {
      return pair.appointment.status !== AppointmentStatus.Unscheduled;
    }
    return !TreatmentStep.isIncomplete(pair.treatmentStep);
  }

  toDragNode(
    group: IGroup<
      ISelectOption<ITreatmentPlanStepPair>,
      WithRef<ITreatmentPlan>
    >
  ): IConsolidatedPlanDragDropNode {
    return {
      parent: group.group,
      items: group.items.map((item) => item.value),
    };
  }

  canAddStep(
    group: IGroup<
      ISelectOption<ITreatmentPlanStepPair>,
      WithRef<ITreatmentPlan>
    >
  ): boolean {
    return isTreatmentPlan(group.group);
  }

  async addStep(
    plan: WithRef<ITreatmentPlan>,
    afterStepRef?: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    await this._addStepAtPosition(plan, StepAddPosition.After, afterStepRef);
  }

  async addStepBefore(
    plan: WithRef<ITreatmentPlan>,
    beforeStepRef: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    await this._addStepAtPosition(plan, StepAddPosition.Before, beforeStepRef);
  }

  isFirstUnscheduledInGroup(
    group: IGroup<
      ISelectOption<ITreatmentPlanStepPair>,
      WithRef<ITreatmentPlan>
    >,
    index: number
  ): boolean {
    if (group.items[index]?.value.status !== 'unscheduled') {
      return false;
    }
    if (index === 0) {
      return true;
    }
    return group.items[index - 1]?.value.status !== 'unscheduled';
  }

  async moveTreatmentsToStep(
    plan: WithRef<ITreatmentPlan>,
    step: WithRef<ITreatmentStep>
  ): Promise<void> {
    await this._moveSelectedTreatments(plan, step.ref);
  }

  async updateSurfaces(
    data: IEditChartableData,
    step: WithRef<ITreatmentStep>
  ): Promise<void> {
    const feeSchedule = await snapshot(
      this._chartStore.currentSchedule$
    );
    return ChartableSurfaceUpdater.updateTreatmentSurfaces(data, [
      new AddTreatmentToStoreStepProvider(
        this._treatmentStepsFacade,
        step,
        feeSchedule
      ),
    ]);
  }

  private async _moveSelectedTreatments(
    plan: WithRef<ITreatmentPlan>,
    targetStepRef: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    if (!this._selectionList) {
      return;
    }
    await this._treatmentProvider.moveSelectedTreatments(
      this._selectionList,
      plan,
      targetStepRef
    );
  }

  private async _handleStepDrag(
    event: IDropEvent<WithRef<ITreatmentPlan>, ITreatmentPlanStepPair>
  ): Promise<void> {
    const movedFromPlan = isTreatmentPlan(event.previous.data.parent);
    const movedToPlan = isTreatmentPlan(event.current.data.parent);
    const [previousPlan, nextPlan] = this._getSourcePlans(event);

    if (previousPlan && !isSameRef(previousPlan, nextPlan)) {
      return;
    }

    const planStepsChangeSource = movedToPlan
      ? event.current.data.items
      : movedFromPlan
        ? event.previous.data.items
        : undefined;

    if (planStepsChangeSource && nextPlan) {
      const updatedSteps = await asyncForEach(
        planStepsChangeSource,
        async (step) =>
          isWithRef(step.treatmentStep)
            ? step.treatmentStep.ref
            : addDoc(
                TreatmentPlan.treatmentStepCol(nextPlan),
                step.treatmentStep
              )
      );

      const combinedStepOrder = this._mergeSteps(nextPlan, updatedSteps);

      await patchDoc(nextPlan.ref, { steps: compact(combinedStepOrder) });
    }
  }

  private async _addStepAtPosition(
    plan: WithRef<ITreatmentPlan>,
    position: StepAddPosition,
    targetStepRef?: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    if (this.stepDisplay.isUnscheduledHidden()) {
      this.stepDisplay.enableUnscheduledFilter();
    }
    const stepRef = await this._createStep(plan, position, targetStepRef);
    await this._moveSelectedTreatments(plan, stepRef);
  }

  private async _createStep(
    plan: WithRef<ITreatmentPlan>,
    position: StepAddPosition,
    targetStepRef?: DocumentReference<ITreatmentStep>
  ): Promise<DocumentReference<ITreatmentStep>> {
    if (targetStepRef && position === StepAddPosition.Before) {
      return this._treatmentPlanFacade.addStepBefore(plan, targetStepRef);
    }
    return this._treatmentPlanFacade.addStep(plan, targetStepRef);
  }

  /**
   * We merge treatment steps with the assumption that incomplete treatment steps - which are the only
   * draggable steps - are at the end of the treatment plan. With this assumption, we can get the last
   * occurence of each treatment step as the new order of steps.
   */
  private _mergeSteps(
    nextPlan: WithRef<ITreatmentPlan>,
    updatedSteps: DocumentReference<ITreatmentStep>[]
  ): DocumentReference<ITreatmentStep>[] {
    const currentStepOrder = nextPlan.steps;
    const combinedSteps = [...currentStepOrder, ...updatedSteps];
    const combinedStepOrder = uniqBy(
      combinedSteps.reverse(),
      (step) => step.path
    ).reverse();
    return combinedStepOrder;
  }

  private _getSourcePlans(
    event: IDropEvent<WithRef<ITreatmentPlan>, ITreatmentPlanStepPair>
  ): [
    WithRef<ITreatmentPlan> | undefined,
    WithRef<ITreatmentPlan> | undefined,
  ] {
    const previousPlan = isTreatmentPlan(event.previous.data.parent)
      ? event.previous.data.parent
      : undefined;
    const nextPlan = isTreatmentPlan(event.current.data.parent)
      ? event.current.data.parent
      : undefined;
    return [previousPlan, nextPlan];
  }
}
